/**
 * EntityLinkingSection - Main container for entity linking UI
 *
 * Displays a unified view with:
 * - Two-column layout (Finch on left, Filevine on right)
 * - Animated bezier curve connections between linked entities
 * - Drag-to-link functionality for manual linking
 * - AI-suggested links with approve/reject controls
 */

import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FinchEntity,
  useEntitySyncApproveLinkSuggestionMutation,
  useEntitySyncCreateLinkMutation,
  useEntitySyncGenerateDataSuggestionsMutation,
  useEntitySyncGenerateLinkSuggestionsMutation,
  useEntitySyncListPairsQuery,
  useEntitySyncRejectLinkSuggestionMutation,
} from '@/services/api/entitySyncService';
import type {
  CollectionItemLinkStatus,
  DetailedFilevineCollectionItemSchema,
  EntityLinkSchema,
  FilevineSectionConfigDataSchema,
  FilevineSectionFieldConfigSchema,
} from '@/services/api/generated/entitySyncService';

import { PollingProvider } from './context';
import type { CanonicalSectionType } from './finch-cards';
import { UnifiedLinkingView } from './unified-view';

interface EntityLinkingSectionProps {
  matterId: string;
  firmId?: string;
  canonicalSectionType: CanonicalSectionType;
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  sectionConfigData?: FilevineSectionConfigDataSchema;
  hideHeader?: boolean;
  className?: string;
}

export const EntityLinkingSection: React.FC<EntityLinkingSectionProps> = ({
  matterId,
  firmId,
  canonicalSectionType,
  fieldConfigs,
  sectionConfigData,
  hideHeader = false,
  className,
}) => {
  // Fetch entity pairs (replaces linking status)
  const {
    data: pairsData,
    isLoading,
    isError,
    refetch,
  } = useEntitySyncListPairsQuery({
    matterId,
    entityType: canonicalSectionType,
    includeUnlinked: true,
  });

  // Transform pairs into separate arrays for backward compatibility with UnifiedLinkingView
  const { finchEntities, filevineEntities, suggestedLinks, approvedLinks } =
    useMemo(() => {
      const pairs = pairsData?.pairs ?? [];

      // Use reduce to collect entities and links from pairs
      const result = pairs.reduce(
        (acc, pair) => {
          // Add Finch entity if present
          if (pair.finch_entity) {
            acc.finchMap.set(pair.finch_entity.id, pair.finch_entity);
          }
          // Add Filevine entity if present
          if (pair.filevine_entity) {
            acc.filevineMap.set(pair.filevine_entity.id, pair.filevine_entity);
          }
          // Add approved or suggested link if present
          if (pair.link) {
            if (
              pair.link.link_status ===
              ('APPROVED' as unknown as CollectionItemLinkStatus)
            ) {
              acc.approved.push(pair.link);
            } else if (
              pair.link.link_status ===
              ('SUGGESTED' as unknown as CollectionItemLinkStatus)
            ) {
              acc.suggested.push(pair.link);
            }
          }
          // Also check link_suggestions array for additional suggestions
          (pair.link_suggestions ?? [])
            .filter(
              (s) =>
                s.link_status ===
                ('SUGGESTED' as unknown as CollectionItemLinkStatus),
            )
            .forEach((s) => acc.suggested.push(s));
          return acc;
        },
        {
          finchMap: new Map<string, FinchEntity>(),
          filevineMap: new Map<string, DetailedFilevineCollectionItemSchema>(),
          suggested: [] as EntityLinkSchema[],
          approved: [] as EntityLinkSchema[],
        },
      );

      return {
        finchEntities: Array.from(result.finchMap.values()),
        filevineEntities: Array.from(result.filevineMap.values()),
        suggestedLinks: result.suggested,
        approvedLinks: result.approved,
      };
    }, [pairsData]);

  // Mutations
  const [approveLinkSuggestion] = useEntitySyncApproveLinkSuggestionMutation();

  const [rejectLinkSuggestion] = useEntitySyncRejectLinkSuggestionMutation();

  const [generateSyncSuggestions, { isLoading: isGeneratingSyncSuggestions }] =
    useEntitySyncGenerateDataSuggestionsMutation();

  const [createLink] = useEntitySyncCreateLinkMutation();

  const [generateLinkSuggestions, { isLoading: isGeneratingLinkSuggestions }] =
    useEntitySyncGenerateLinkSuggestionsMutation();

  // Handle generate link suggestions
  const handleGenerateSuggestions = useCallback(async () => {
    try {
      await generateLinkSuggestions({
        matterId,
        entityType: canonicalSectionType,
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate link suggestions:', error);
    }
  }, [generateLinkSuggestions, matterId]);

  // Handle generate sync suggestions (for syncing data between linked entities)
  const handleGenerateSyncSuggestions = useCallback(async () => {
    try {
      await generateSyncSuggestions({
        matterId,
        entityType: canonicalSectionType,
        generateSuggestionsRequestSchema: {
          supersede_existing: true,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate sync suggestions:', error);
    }
  }, [generateSyncSuggestions, matterId]);

  // Handle approve link suggestion
  const handleApprove = useCallback(
    async (linkId: string) => {
      try {
        await approveLinkSuggestion({
          matterId,
          entityType: canonicalSectionType,
          suggestionId: linkId,
        }).unwrap();
      } catch (error) {
        console.error('Failed to approve link:', error);
      }
    },
    [approveLinkSuggestion, matterId],
  );

  // Handle reject link suggestion
  const handleReject = useCallback(
    async (linkId: string) => {
      try {
        await rejectLinkSuggestion({
          matterId,
          entityType: canonicalSectionType,
          suggestionId: linkId,
        }).unwrap();
      } catch (error) {
        console.error('Failed to reject link:', error);
      }
    },
    [rejectLinkSuggestion, matterId],
  );

  // Handle create link (from drag-to-link)
  const handleCreateLink = useCallback(
    async (finchEntityId: string, filevineEntityId: string) => {
      try {
        await createLink({
          matterId,
          entityType: canonicalSectionType,
          createLinkRequestSchema: {
            finch_entity_id: finchEntityId,
            filevine_entity_id: filevineEntityId,
          },
        }).unwrap();
      } catch (error) {
        console.error('Failed to create link:', error);
      }
    },
    [createLink, matterId],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="flex items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 p-8">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          <span className="ml-2 text-zinc-500">Loading linking status...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="mt-2 font-medium text-red-700">
            Failed to load linking status
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="mt-4"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Entity arrays are now computed in useMemo above from pairsData

  return (
    <PollingProvider>
      <div className={cn('space-y-6', className)}>
        {/* Unified Linking View - Two-column entity view */}
        <UnifiedLinkingView
          matterId={matterId}
          firmId={firmId}
          canonicalSectionType={canonicalSectionType}
          fieldConfigs={fieldConfigs}
          sectionConfigData={sectionConfigData}
          finchEntities={finchEntities}
          filevineEntities={filevineEntities}
          suggestedLinks={suggestedLinks}
          approvedLinks={approvedLinks}
          onApprove={handleApprove}
          onReject={handleReject}
          onCreateLink={handleCreateLink}
          isGeneratingSuggestions={isGeneratingLinkSuggestions}
          onGenerateSuggestions={handleGenerateSuggestions}
          isGeneratingSyncSuggestions={isGeneratingSyncSuggestions}
          onGenerateSyncSuggestions={handleGenerateSyncSuggestions}
          hideHeader={hideHeader}
        />
      </div>
    </PollingProvider>
  );
};

export default EntityLinkingSection;
